import { command, query } from '$app/server';
import { getRequestEvent } from '$app/server';
import { supabaseAdmin } from '#lib/server/supabaseAdmin.js';
import { getYesterdayUTC, parseDate } from '~/utils/dates';
import type { DailyGameState, DailyMode, GameState, StreakResponse, TwistGameState } from '~/types';
import { getRelativeDate } from '~/utils/dates';

const getStreak = (userId: string, mode: DailyMode) =>
  supabaseAdmin
    .from('daily_streaks')
    .select('*')
    .eq('user_id', userId)
    .eq('mode', mode)
    .single();

export const getDailyStreak = query(
  'unchecked',
  async (mode: DailyMode): Promise<StreakResponse> => {
    const { locals } = getRequestEvent();
    if (!locals.user) throw new Error('not authenticated');
    const result = await getStreak(locals.user.id, mode);
    if (result.error) throw new Error(result.error.message);
    return result.data as StreakResponse;
  },
);

export const saveDailyGame = command(
  'unchecked',
  async (game: DailyGameState): Promise<StreakResponse | null> => {
    const { locals } = getRequestEvent();
    const userId = locals.user?.id ?? locals.userId;

    const prevBest = await supabaseAdmin.rpc('get_best_daily', {
      curr_user_id: userId,
      curr_date: game.date,
      curr_mode: game.mode,
      curr_key: game.key,
    });

    await supabaseAdmin
      .from('daily_analytics')
      .insert({ ...game, user_id: locals.userId });

    if (!locals.user) return null;

    let streak;
    const yesterday = getYesterdayUTC();
    if (!prevBest.data && +parseDate(game.date)! >= +yesterday && !game.key) {
      streak = await supabaseAdmin.rpc('update_daily_streak', {
        won: (game.stars ?? 0) > 0,
        curr_user_id: locals.user.id,
        curr_game_id: game.id,
        curr_mode: game.mode,
      });
    } else {
      streak = await getStreak(locals.user.id, game.mode);
    }

    if (streak.error) throw new Error(streak.error.message);
    return streak.data as StreakResponse;
  },
);

export const saveClassicGame = command(
  'unchecked',
  async (game: GameState): Promise<number> => {
    const { locals } = getRequestEvent();

    const res = await supabaseAdmin
      .from('classic_analytics')
      .insert({ ...game, user_id: locals.userId });

    if (res.error) throw new Error(res.error.message);

    const pos = await supabaseAdmin.rpc('get_classic_leaderboard_position', {
      curr_score: game.score,
      curr_date: new Date(),
      date_cutoff: getRelativeDate('week'),
    });

    return pos.data as number;
  },
);

const getTwistStreak = (userId: string, mode: DailyMode) =>
  supabaseAdmin
    .from('twist_streaks')
    .select('*')
    .eq('user_id', userId)
    .eq('mode', mode)
    .single();

export const saveTwistGame = command(
  'unchecked',
  async (game: TwistGameState): Promise<StreakResponse | null> => {
    const { locals } = getRequestEvent();

    const prev = await supabaseAdmin
      .from('twist_analytics')
      .select('*', { count: 'exact', head: true })
      .eq('id', game.id)
      .single();

    await supabaseAdmin
      .from('twist_analytics')
      .insert({ ...game, user_id: locals.userId })
      .throwOnError();

    if (!locals.user) return null;

    let streak;
    const yesterday = getYesterdayUTC();
    if (!prev.data?.count && !game.key && +parseDate(game.date)! >= +yesterday) {
      streak = await supabaseAdmin
        .from('twist_streaks')
        .update({
          user_id: locals.user.id,
          mode: game.mode,
          last_game_id: game.id,
          last_game_date: game.date,
        })
        .match({ user_id: locals.user.id, mode: game.mode });
    } else {
      streak = await getTwistStreak(locals.user.id, game.mode);
    }

    if (streak.error) throw new Error(streak.error.message);
    return streak.data as StreakResponse;
  },
);
